/**
 * Deterministic randomness. Every generated fixture draws from a seeded
 * stream, so the data is identical on every machine and every run.
 */
export type Rng = () => number;

/** mulberry32: small, fast, and good enough for fixtures. */
export function rng(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A stable 32-bit hash of a string, for seeding per-entity streams. */
export function hash(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function pick<T>(r: Rng, items: readonly T[]): T {
  return items[Math.floor(r() * items.length)] as T;
}

export function int(r: Rng, min: number, max: number): number {
  return min + Math.floor(r() * (max - min + 1));
}

/** Weighted choice over `[item, weight]` pairs. */
export function weighted<T>(r: Rng, entries: readonly (readonly [T, number])[]): T {
  let total = 0;
  for (const [, w] of entries) total += w;
  let x = r() * total;
  for (const [item, w] of entries) {
    x -= w;
    if (x <= 0) return item;
  }
  return entries[entries.length - 1]?.[0] as T;
}

/** Precomputed weighted sampler, for hot loops. */
export function sampler<T>(entries: readonly (readonly [T, number])[]): (r: Rng) => T {
  const items = entries.map(([item]) => item);
  const cumulative: number[] = [];
  let total = 0;
  for (const [, w] of entries) {
    total += w;
    cumulative.push(total);
  }
  return (r) => {
    const x = r() * total;
    let lo = 0;
    let hi = cumulative.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if ((cumulative[mid] as number) < x) lo = mid + 1;
      else hi = mid;
    }
    return items[lo] as T;
  };
}

/** Log-normal around a median, for latencies. */
export function logNormal(r: Rng, median: number, spread = 0.5): number {
  const u = Math.max(r(), 1e-9);
  const v = r();
  const z = Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  return median * Math.exp(z * spread);
}

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/** A ULID-shaped id: sortable by time, like the real ones will be. */
export function ulid(ms: number, r: Rng): string {
  let time = "";
  let t = Math.floor(ms);
  for (let i = 0; i < 10; i += 1) {
    time = CROCKFORD[t % 32] + time;
    t = Math.floor(t / 32);
  }
  let rand = "";
  for (let i = 0; i < 16; i += 1) rand += CROCKFORD[Math.floor(r() * 32)];
  return time + rand;
}

/** Lower-case alphanumeric token of a given length. */
export function token(
  r: Rng,
  length: number,
  alphabet = "abcdefghijklmnopqrstuvwxyz0123456789",
) {
  let out = "";
  for (let i = 0; i < length; i += 1)
    out += alphabet[Math.floor(r() * alphabet.length)];
  return out;
}
